// currentArea state is stored in navigationAPI.js (store)
// https://mccglc.atlassian.net/l/c/y4sqm07q for DOCS

import { getRoomPositions } from '@/functions/d3_geometry/d3Functions-demo.js';

const state = {
  // current area data -----------------------------------------------
  currentArea: {}, //users current area
  // area children (spaces) ------------------------------------------
  spaces: [],
  // error handling
  errorEnteringArea: false,

  enterJumpSpace: null,
  hideUnauthorisedPortals: false,
  authorisedLocations: [], //authorised area children locations (loaded on enter area, doesnt come from message queue updates) - used for jump space access/visibility
};

const getters = {
  getCurrentAreaData: (state) => state.currentArea,
  // AREA data ------------------------------------------
  getCurrentAreaId: (state,getters) => {
    let locationType = getters.getCurrentLocationType
    return locationType === 'area' ? getters.getCurrentLocationId || '' : getters.getCurrentUsersAreaId || ''
  },
  getCurrentAreaFirebaseId: (state,getters) => {
    return getters.getCurrentAreaData?.sourceId || ''
  },
  getCurrentAreaLogo: (state,getters) => {
    return getters.getCurrentAreaData?.logo || ''
  },
  getCurrentAreaCompanyName: (state,getters) => {
    return getters.getCurrentAreaData?.companyName || ''
  },
  getCurrentAreaCreator: (state,getters) => {
    return getters.getCurrentAreaData?.creator || ''
  },
  getCurrentAreaLayout: (state,getters) => {
    return getters.getCurrentAreaData?.layout || ''
  },
  getCurrentAreaType: (state,getters) => { //private/open space
    return getters.getCurrentAreaData?.areaType || ''
  },
  isOpenSpace: (state,getters) => { //private/open space
    return getters.getCurrentAreaType === 'open'
  },
  isPrivateSpace: (state,getters) => { //private/open space
    return getters.getCurrentAreaType === 'private'
  },

  // AREA LOCATION data -----------------------------------------
  getCurrentAreaLocationData: (state,getters) => {
    let locationType = getters.getCurrentLocationType
    return locationType === 'area' ? getters.getCurrentLocationData || {} : getters.getCurrentAreaData?.location || {}
  },
  getCurrentAreaActiveUsersNo: (state,getters) => {
    return getters.getCurrentAreaLocationData?.online || 0
  },
  getCurrentAreaTitle: (state,getters) => {
    return getters.getCurrentAreaLocationData?.name || ''
  },
  getCurrentAreaTitleNormalised: (state,getters) => (title) => {
    if(!title) {
      title = getters.getCurrentAreaTitle
    }
    return title.toLowerCase().replaceAll(' ','-') || ''
  },
  getCurrentAreaChildren: (state,getters) => {
    return getters.getCurrentAreaLocationData?.children || []
  },
  // CURRENT AREA HUB + LOUNGE data -----------------------------
  getCurrentAreaHubId: (state, getters) => {
    return getters.getCurrentAreaData?.location?.hubId || ''
  },
  getCurrentAreaLoungeId: (state, getters) => {
    return getters.getCurrentAreaData?.location?.loungeId || ''
  },

  // AREA INFO data --------------------------------------------
  getCurrentAreaInfo: (state,getters) => {
    return getters.getCurrentAreaData?.info || null
  },

  // AREA BANNER data --------------------------------------------
  getCurrentAreaBanner: (state,getters) => {
    return getters.getCurrentAreaData?.banner || null
  },

  // AREA COVER data --------------------------------------------
  getCurrentAreaCover: (state,getters) => {
    return getters.getCurrentAreaInfo?.coverImage || null
  },

  // AREA COLOUR SCHEME data -----------------------------------
  getCurrentAreaColourScheme: (state,getters) => {
    return getters.getCurrentAreaData?.colourScheme || null
  },
  getCurrentAreaPrimaryColour: (state,getters) => {
    return getters.getCurrentAreaColourScheme?.primaryColour || ''
  },

  // AREA FEATURES data -----------------------------------------
  getCurrentAreaFeaturesData: (state,getters) => {
    return getters.getCurrentAreaData?.features || {}
  },
  getCurrentAreaPremiumFeaturesEnabled: (state,getters) => {
    return getters.getCurrentAreaFeaturesData?.premiumFeature || false
  },
  getCurrentAreaUserLimit: (state,getters) => {
    return getters.getCurrentAreaFeaturesData?.userLimit || 10
  },
  getCurrentAreaCalendarIntegration: (state,getters) => {
    return getters.getCurrentAreaFeaturesData?.calendarFeature || false
  },
  getAreaStopSpacesBackup: (state,getters) => {
    return getters.getCurrentAreaFeaturesData?.spaceCreationFeature || true
  },

  // area children (spaces) ------------------------------------------
  getSpaces: (state) => {
    return state.spaces;
  },
  // get space data for item in spaces array from space id
  getRoomData: (state) => (id) => {
    let matches = state.spaces.filter(space => space.id === id || space.sourceId === id)
    return matches.length ? matches[0] : null
  },
  getLoungeRoomData: (state,getters) => {
    let loungeId = getters.getCurrentAreaLoungeId
    return getters.getRoomData(loungeId)
  },
  getHubRoomData: (state,getters) => {
    let hubId = getters.getCurrentAreaHubId
    return getters.getRoomData(hubId)
  },

  // error handling
  getIfErrorEnteringArea: (state) => {
    return state.errorEnteringArea;
  },

  // jump spaces
  enterJumpSpace: (state) => {
    return state.enterJumpSpace
  },
  hideUnauthorisedPortals: (state) => state.hideUnauthorisedPortals,
  authorisedLocations: (state) => state.authorisedLocations,
  isAuthorisedLocation: (state, getters) => (id) => getters.authorisedLocations.filter(l => l === id)[0] ? true : false,
};

const mutations = {
  // current area --------------------------------------------
  setCurrentArea(state, data) {
    state.currentArea = data;
  },
  // spaces --------------------------------------------------
  setSpaces(state, data) {
    state.spaces = data;
  },

  // area info -----------------------------------------------
  setCurrentAreaInfo(state, data){
    state.currentArea.info = data;
  },

  // error handling -----------------------------------------------
  setErrorEnteringArea(state, data){
    state.errorEnteringArea = data;
  },

  // jump space  -----------------------------------------------
  ENTER_JUMP_SPACE(state, data){
    state.enterJumpSpace = data
  },
  AUTHORISED_LOCATIONS(state, data) {
    state.authorisedLocations = data
  },
};

const actions = {
  // CURRENT AREA DATA ---------------------------------------------
  async updateCurrentArea({rootGetters, getters,commit,dispatch},{data}) {
    let currentAreaData = getters.getCurrentAreaData || {}

    let updateData = data.location ? {...data} : {...currentAreaData,location: {...data}}
    commit('setCurrentArea',updateData)
    setTimeout(() => dispatch('recalculateRoomLayout'), 500)
    dispatch('root/updateCssSettings', updateData.colourScheme)

    if(getters['routing/routeName'] === 'area'){
      let matchedRoom = false
      let currentRoom = getters.getCurrentSpacePageData
      if(currentRoom?.id) {
        for(let room of getters.getSpaces) if(room.id === currentRoom.id) {
          matchedRoom = true
          currentRoom.name = room.name
          currentRoom.location.name = room.name
          currentRoom.location.online = room.online
          currentRoom.location.passwordEnabled = room.passwordEnabled
          currentRoom.location.lobbyEnabled = room.lobbyEnabled
          currentRoom.passwordEnabled = room.passwordEnabled
          currentRoom.lobbyEnabled = room.lobbyEnabled
          dispatch('updateCurrentSpacePage', currentRoom)
          break
        }
        if(!matchedRoom && rootGetters['routing/getParamValue']('room')){
          dispatch('stopViewingRoom')
          dispatch('popups/show/error', {message: 'The room you were viewing has been deleted.'})
        }
      }
    }

    // re-do search - users moving around could affect their whereabouts
    if(getters['search/query']) dispatch('search/doSearch');
  },
  async clearCurrentAreaData({commit}) {
    commit('setCurrentArea', {})
    commit('setSpaces', [])
  },
  // ENTER AREA ---------------------------------------------------
  async enterCurrentArea({getters,dispatch}) {
    console.log('enter current area')
    let currentAreaId = getters.getCurrentAreaId
    if(currentAreaId) {
      return await dispatch('enterAreaHandler',currentAreaId)
    }
    dispatch('enterSwitchAreaPageHandler')
    return
  },
  async enterAreaHandler({getters, dispatch},id) {
    if(!id) return dispatch('enterAreaCallback', {error: 'no area id'})

    if(getters['current_user/isGuest'] && !getters.isOpenSpace) {
      console.log('skip enter area if guest joining room')
      return {error: 'not space member'}
    }

    let res = await dispatch('api/nav/enterLocation',id)
    await dispatch('enterAreaCallback', res)
    return res
  },
  async enterAreaHandlerOnRoomLoad({commit, dispatch},id) { //when joining room from lobby - don't kick if no access to  area but access to room (ie-guest)
    if(!id) return
    let res = await dispatch('api/nav/enterLocation',id)
    if(res.success) {
      await dispatch('enterAreaCallback', res) //don't do callback if fails so doesn't kick (guest) user out
      await dispatch('analytics/enterSpace', 'direct')
    } else {
      commit('setErrorEnteringArea', true)
    }
    return res
  },
  async enterAreaHandlerAsGuest({commit, dispatch},id) { //when joining room from lobby - don't kick if no access to  area but access to room (ie-guest)
    if(!id) return
    let res = await dispatch('api/nav/enterLocation',id)
    if(res.success) {
      await dispatch('enterAreaCallback', res) //don't do callback if fails so doesn't kick (guest) user out
      await dispatch('analytics/enterSpace', 'direct')
    } else {
      dispatch('popups/nav/errorEnteringSpace')
      commit('setErrorEnteringArea', true)
    }
    return res
  },
  async enterAreaCallback({getters, commit, dispatch},res) {
    if(res.success) {
      let data = res.success
      await dispatch('updateLocation',[data])

      // update area data with data from api
      await dispatch('updateCurrentArea', {data})
      dispatch('clearCurrentSpacePage')
      dispatch('search/clearSpaceParticipants')
      if(getters.enterJumpSpace === data.id) dispatch('updateEnterJumpSpace', null)
      dispatch('calcAuthorisedLocations', data)
    } else {
      dispatch('popups/nav/errorEnteringSpace')
      dispatch('routing/goToHome')
      commit('setErrorEnteringArea', true)
    }

    return
  },
  calcAuthorisedLocations({commit}, data) {
    let authorised = []
    let children = data?.location?.children || []
    children.forEach(c => {
      if(c.isAuthorised) authorised.push(c.id)
    })
    commit('AUTHORISED_LOCATIONS', authorised)
  },
  updateEnterJumpSpace({commit, dispatch}, val) {
    if(val) dispatch('analytics/enterJumpSpace', [val, 'direct'], {root: true})
    commit('ENTER_JUMP_SPACE', val)
  },
  async handleEnterSpacePortal({getters, commit, dispatch}, id) {
    if(!id) id = getters.getCurrentSpacePageData?.id || ''
    commit('routing/BLOCK_GET_AREA_DATA', true, {root: true})
    await dispatch('stopViewingRoom')
    let res = await dispatch('api/nav/joinRoom', id)
    let data = res?.success
    if(data) {
      //animate out
      dispatch('updateEnterJumpSpace', id)
      await new Promise(resolve => setTimeout(resolve, 700))
      //animate in
      await dispatch('clearCurrentSpacePage')
      await dispatch('updateLocation',[data])
      dispatch('calcAuthorisedLocations', data)
      dispatch('routing/goToArea', data.id)
    } else {
      dispatch('updateEnterJumpSpace', null)
      dispatch('popups/nav/errorEnteringSpacePortal')
    }
    return res
  },

  async fetchAreaDataInBackground({getters,dispatch},id) {
    if(!id) {
      id = getters.getCurrentAreaId
    }
    console.log('fetch area data in background', id)
    let res = await dispatch('api/nav/getLocation',id)
    if(res.success) {
      let data = res.success
      dispatch('updateWhereabouts',{type: 'enter-location', data})
      // update area data with data from api
      await dispatch('updateCurrentArea', {data})
    } else {
      console.log('error fetching area data in background')
    }
  },
  async fetchNewAreaDataAfterUpdate({dispatch}) {
    dispatch('fetchAreaDataInBackground');
  },

  // spaces ------------------------------------------------------------
  async recalculateRoomLayout({getters, commit}) {
    let spaces = getters.getCurrentAreaChildren.filter(space => space.locationType === 'space')

    if(!spaces.length) return;

    spaces.unshift({
      spaceType: 'create_space',
      baseAngle: 0,
      id: 'create',
      locationType: 'space',
      name: '',
      online: 0
    })

    commit('setSpaces', getRoomPositions(spaces, getters['viewport/isMobile']))
  },
};

export default {
  state,
  getters,
  mutations,
  actions
};
